import { Logic } from '~common/ui-logic/logic'
import { executeTask } from '~common/ui-logic/tasks'
import { TaskState } from '../../../../services/cache/types'
import type { AppContextType } from '~/app-context'

export interface LoginComponentDependencies {
    onSuccess: (params: { newUser: boolean }) => void
    onError?: (error: string) => void
}

export type LoginComponentState = {
    loadState: TaskState
    error: string | null
    email?: string
    popupBlocked: boolean
}

export class LoginComponentLogic extends Logic<
    LoginComponentDependencies & AppContextType,
    LoginComponentState
> {
    getInitialState = (): LoginComponentState => ({
        loadState: 'pristine',
        error: null,
        email:
            this.deps.services.cache.getKey('authState')?.data?.email ??
            undefined,
        popupBlocked: false,
    })

    async initialize() {
        const blocked = this.checkPopupBlocked()
        this.setState({
            ...this.state,
            popupBlocked: blocked,
        })
    }

    private checkPopupBlocked(): boolean {
        try {
            const testPopup = window.open(
                '',
                '_blank',
                'width=1,height=1,top=0,left=0',
            )
            if (
                !testPopup ||
                testPopup.closed ||
                typeof testPopup.closed === 'undefined'
            ) {
                return true
            }
            testPopup.close()
            return false
        } catch {
            return true
        }
    }

    recheckPopupBlocked = () => {
        const blocked = this.checkPopupBlocked()
        this.setState({
            ...this.state,
            popupBlocked: blocked,
        })
    }

    handleGoogleSignIn = async () => {
        await executeTask(this, 'loadState', async () => {
            try {
                // Proceed with full sign-in
                const result = await this.deps.services.auth.loginWithGoogle()

                if (!result) {
                    throw new Error('Failed to login with Google')
                }

                if (result?.newUser) {
                    this.deps.onSuccess({
                        newUser: true,
                    })
                } else if (result?.newUser === false) {
                    // Check if existing user has completed onboarding
                    const currentUser =
                        await this.deps.services.auth.getLoggedInUser()
                    if (!currentUser) {
                        throw new Error('Failed to get logged in user')
                    }

                    this.deps.onSuccess({
                        newUser: false,
                    })
                }
            } catch (error) {
                let errorMessage: string
                if (
                    error instanceof Error &&
                    error.message.includes('popup_closed_by_user')
                ) {
                    errorMessage = ''
                } else {
                    errorMessage =
                        error instanceof Error
                            ? error.message
                            : 'Sign-in failed. Please try again.'
                }
                this.setState({
                    ...this.state,
                    loadState: 'pristine',
                    error: errorMessage,
                })
            }
        })
    }
}
